interface RGB {
  r: number;
  g: number;
  b: number;
}

function rgbToHex({ r, g, b }: RGB): string {
  return (
    "#" +
    [r, g, b]
      .map((c) => Math.round(c).toString(16).padStart(2, "0"))
      .join("")
  );
}

function colorDistance(a: RGB, b: RGB): number {
  return Math.sqrt((a.r - b.r) ** 2 + (a.g - b.g) ** 2 + (a.b - b.b) ** 2);
}

function getVibrancy({ r, g, b }: RGB): number {
  const max = Math.max(r, g, b) / 255;
  const min = Math.min(r, g, b) / 255;
  const saturation = max === 0 ? 0 : (max - min) / max;
  const lightness = (max + min) / 2;
  return saturation * (1 - Math.abs(lightness - 0.5) * 2);
}

function kMeans(pixels: RGB[], k: number, iterations: number = 10): RGB[] {
  const centroids: RGB[] = [];
  const step = Math.floor(pixels.length / k);
  for (let i = 0; i < k; i++) {
    centroids.push({ ...pixels[i * step] });
  }

  for (let iter = 0; iter < iterations; iter++) {
    const clusters: RGB[][] = Array.from({ length: k }, () => []);

    for (const pixel of pixels) {
      let minDist = Infinity;
      let minIdx = 0;
      for (let i = 0; i < k; i++) {
        const dist = colorDistance(pixel, centroids[i]);
        if (dist < minDist) {
          minDist = dist;
          minIdx = i;
        }
      }
      clusters[minIdx].push(pixel);
    }

    for (let i = 0; i < k; i++) {
      if (clusters[i].length === 0) continue;
      centroids[i] = {
        r: clusters[i].reduce((s, p) => s + p.r, 0) / clusters[i].length,
        g: clusters[i].reduce((s, p) => s + p.g, 0) / clusters[i].length,
        b: clusters[i].reduce((s, p) => s + p.b, 0) / clusters[i].length,
      };
    }
  }

  return centroids;
}

export async function extractColors(imageUrl: string, k: number = 5): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const size = 100;
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas not supported"));

      ctx.drawImage(img, 0, 0, size, size);
      const imageData = ctx.getImageData(0, 0, size, size).data;

      const pixels: RGB[] = [];
      for (let i = 0; i < imageData.length; i += 4) {
        const r = imageData[i];
        const g = imageData[i + 1];
        const b = imageData[i + 2];
        const a = imageData[i + 3];
        if (a < 128) continue;
        if (r > 240 && g > 240 && b > 240) continue;
        if (r < 15 && g < 15 && b < 15) continue;
        pixels.push({ r, g, b });
      }

      if (pixels.length < k) {
        resolve(pixels.map(rgbToHex));
        return;
      }

      const centroids = kMeans(pixels, k);
      centroids.sort((a, b) => getVibrancy(b) - getVibrancy(a));
      resolve(centroids.map(rgbToHex));
    };

    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = imageUrl;
  });
}
